import type { AnyClientAgent, ServerAgentWithPostCommitHookContext } from "./agent";
import { createVersionedRegistry } from "../common";

/**
 * Agent registry interface that supports versioned lookups.
 *
 * @param agentType - The agent type identifier
 * @param version - Optional version number. If provided, returns that specific version.
 *                  If not provided, returns the latest registered version.
 */
export type AgentRegistry<Agent extends AnyClientAgent = AnyClientAgent> = {
  /**
   * Get an agent by type and optional version.
   * @param agentType - The agent type identifier
   * @param version - Optional version number. If omitted, returns the latest version.
   * @throws Error if agent type not found, or if specific version requested but not found
   */
  getAgent: (agentType: string, version?: number) => Agent;

  /**
   * Get the latest version number for an agent type.
   * @param agentType - The agent type identifier
   * @returns The latest version number
   * @throws Error if agent type not found
   */
  getLatestVersion: (agentType: string) => number;

  /**
   * Check if a specific version is registered for an agent type.
   * @param agentType - The agent type identifier
   * @param version - The version to check
   * @returns true if the version is registered
   */
  hasVersion: (agentType: string, version: number) => boolean;

  /**
   * Get all registered versions for an agent type.
   * @param agentType - The agent type identifier
   * @returns Array of version numbers, sorted newest first
   */
  getVersions: (agentType: string) => number[];
};

export type AnyAgentRegistry = AgentRegistry<AnyClientAgent>;

type AgentRegisterable<Agent extends AnyClientAgent> = AgentRegistry<Agent> & {
  registerAgent: <T extends Agent>(agent: T) => void;
};

function createAgentRegistry<Agent extends AnyClientAgent>(
  agents?: Agent[],
): AgentRegisterable<Agent> {
  const baseRegistry = createVersionedRegistry<Agent>(
    {
      getType: (agent) => agent.agentType,
      getVersion: (agent) => agent.version,
      entityName: "Agent",
    },
    agents,
  );

  return {
    registerAgent: baseRegistry.register,
    getAgent: baseRegistry.get,
    getLatestVersion: baseRegistry.getLatestVersion,
    hasVersion: baseRegistry.hasVersion,
    getVersions: baseRegistry.getVersions,
  };
}

export const createServerAgentRegistry = <PostCommitHookContext>() =>
  createAgentRegistry<ServerAgentWithPostCommitHookContext<PostCommitHookContext>>();

export const createClientAgentRegistry = () => createAgentRegistry<AnyClientAgent>();

export const createUnusableAgentRegistry = (): AgentRegistry => {
  return {
    getAgent: () => {
      throw new Error("This agent registry is not usable");
    },
    getLatestVersion: () => {
      throw new Error("This agent registry is not usable");
    },
    hasVersion: () => {
      throw new Error("This agent registry is not usable");
    },
    getVersions: () => {
      throw new Error("This agent registry is not usable");
    },
  };
};
